import { useEffect, useMemo, useRef, useState } from "react"
import { debounce } from "lodash"
import { type Query as TCubeQuery } from "@cubejs-client/core"
import { Button } from "~/ui-rtk/components/ui/controls"
import ComboboxInput, {
  type TComboboxOption,
} from "~/ui-rtk/components/ui/controls/ComboboxInput"
import { cubeQuery } from "~/ui-rtk/hooks/cube"
import { getPivotData } from "~/ui-rtk/utils/cube"

const LOCATION_DIM = "company_shopify_inventory_events.location_id"
const SKU_MEASURE = "company_shopify_inventory_events.unique_variants"

// All-time location list (no date range) so the picker stays stable regardless
// of the selected reporting window. Sorted by distinct-SKU count so the largest
// (likely primary) location surfaces first to help prioritization.
const LOCATION_OPTIONS_QUERY: TCubeQuery = {
  dimensions: [LOCATION_DIM],
  measures: [SKU_MEASURE],
  order: { [SKU_MEASURE]: "desc" },
}

const COMMIT_DEBOUNCE_MS = 400

type TLocationFilterProps = {
  value: string
  onChange: (value: string) => void
}

export default function LocationFilter({
  value,
  onChange,
}: TLocationFilterProps) {
  const { result, isLoading } = cubeQuery(LOCATION_OPTIONS_QUERY)

  // Responsive local input; committing to the URL-backed filter is debounced so
  // typing/pasting an id doesn't fire a cube query per keystroke. `value` only
  // changes externally (commit, clear, navigation), so this sync doesn't fight
  // local typing.
  const [input, setInput] = useState(value)
  useEffect(() => {
    setInput(value)
  }, [value])

  // Read onChange through a ref so the debounced fn stays stable even though the
  // parent's setParams-derived callback is recreated each render.
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  const commit = useMemo(
    () =>
      debounce((next: string) => onChangeRef.current(next), COMMIT_DEBOUNCE_MS),
    [],
  )
  useEffect(() => () => commit.cancel(), [commit])

  const handleInput = (next: string) => {
    setInput(next)
    commit(next.trim())
  }

  const handleClear = () => {
    commit.cancel()
    setInput("")
    onChange("")
  }

  const options = useMemo<TComboboxOption[]>(() => {
    if (!result) return []
    const rows = getPivotData(result, LOCATION_OPTIONS_QUERY) as Array<
      Record<string, unknown>
    >
    return rows
      .map((row, idx) => {
        const id = String(row[LOCATION_DIM] ?? "")
        const skus = Number(row[SKU_MEASURE] ?? 0)
        return { id: idx + 1, value: id, label: `${id} · ${skus} SKUs` }
      })
      .filter(option => option.value)
  }, [result])

  return (
    <div className="flex items-stretch gap-system-xs">
      <div className="w-60">
        <ComboboxInput
          value={input}
          onChange={handleInput}
          options={options}
          placeholder={isLoading ? "Loading locations…" : "Primary location ID"}
        />
      </div>
      {value ? (
        <Button variant={{ variant: "text" }} size="sm" onClick={handleClear}>
          Clear
        </Button>
      ) : null}
    </div>
  )
}
